import { useEffect, useState } from "react";
import { MessageCircle, X, Sparkles, Send, Phone } from "lucide-react";
import { Button } from "../ui/button";
import ApplyDialog from "./ApplyDialog";
import { COLLEGE } from "../../lib/constants";

const cannedReplies = [
  "Our admissions team is online. For instant help, please call +91 78159 10875.",
  "B.Tech 2026 admissions are now open — code MGHA across EAPCET/ECET/ICET/PGECET/POLYCET.",
  "Highest package this year was 36 LPA. Multiple students secured 20+ LPA offers.",
];

export const Extras = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! I'm MEGHA — the MIETW admissions assistant. Ask me about courses, fees or placements." },
  ]);
  const [input, setInput] = useState("");

  const send = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { from: "user", text: userMsg }]);
    setInput("");
    setTimeout(() => {
      const reply = cannedReplies[Math.floor(Math.random() * cannedReplies.length)];
      setMessages((m) => [...m, { from: "bot", text: reply }]);
    }, 700);
  };

  return (
    <>
      {/* WhatsApp floating button */}
      <a
        href={`https://wa.me/${COLLEGE.whatsapp}?text=${encodeURIComponent("Hi, I'd like to apply for B.Tech 2026 at MIETW.")}`}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl shadow-emerald-500/40 hover:scale-110 transition-transform animate-float"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor" aria-hidden>
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.945 2.722.945.93 0 2.064-.86 2.522-1.633.143-.245.243-.516.243-.802 0-.36-1.6-1.232-1.85-1.232M16.32 0c-8.752 0-15.84 7.09-15.84 15.84 0 2.85.756 5.527 2.064 7.847L0 32l8.34-2.7c2.232 1.226 4.78 1.96 7.495 1.96 8.752 0 15.84-7.09 15.84-15.84S25.07.005 16.32 0m0 28.747c-2.706 0-5.244-.84-7.395-2.275l-5.18 1.652 1.682-4.97c-1.59-2.166-2.52-4.91-2.52-7.314 0-7.345 5.998-13.345 13.413-13.345 7.418 0 13.4 5.998 13.4 13.398 0 7.398-5.984 13.398-13.4 13.398z" />
        </svg>
      </a>

      {/* Chatbot trigger */}
      <button
        onClick={() => setChatOpen(true)}
        data-testid="chatbot-trigger"
        className="fixed bottom-24 md:bottom-6 right-20 md:right-24 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-megha-primary to-megha-accent text-white flex items-center justify-center shadow-2xl shadow-blue-900/30 hover:scale-110 transition-transform"
        aria-label="Open MIETW assistant"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-megha-highlight border-2 border-white animate-pulse" />
      </button>

      {/* Chatbot drawer */}
      {chatOpen && (
        <div className="fixed bottom-44 md:bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[360px] bg-white rounded-3xl shadow-2xl shadow-blue-900/25 border border-slate-100 overflow-hidden" data-testid="chatbot-panel">
          <div className="bg-gradient-to-r from-megha-primary to-megha-accent p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-megha-highlight" />
              </div>
              <div>
                <div className="font-heading font-extrabold leading-tight">MEGHA Assistant</div>
                <div className="text-[11px] text-blue-50/80">MIETW Admissions Bot · Beta</div>
              </div>
            </div>
            <button onClick={() => setChatOpen(false)} aria-label="Close" className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 h-[260px] overflow-y-auto bg-slate-50 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl ${
                    m.from === "user" ? "bg-megha-primary text-white rounded-br-md" : "bg-white text-megha-text rounded-bl-md border border-slate-100"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-slate-100 flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about admissions…"
              className="flex-1 h-10 rounded-full bg-slate-50 border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-megha-accent"
            />
            <Button
              data-testid="chatbot-send"
              onClick={send}
              className="h-10 w-10 p-0 rounded-full bg-megha-primary hover:bg-megha-accent"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md" data-testid="mobile-sticky-cta">
        <div className="grid grid-cols-2 gap-2 p-3">
          <a
            href={`tel:${COLLEGE.phones[0].replace(/\s/g, "")}`}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-full border-2 border-megha-primary text-megha-primary font-bold text-sm"
            data-testid="mobile-call-cta"
          >
            <Phone className="w-4 h-4" /> Call
          </a>
          <ApplyDialog
            trigger={
              <Button
                data-testid="mobile-apply-cta"
                className="h-11 rounded-full bg-megha-highlight text-megha-text font-bold text-sm hover:opacity-95"
              >
                Apply Now 2026
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Extras;
